import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import { bugReportsAPI } from '../api/api';
import { Bug, Filter, RefreshCcw, ExternalLink, X, GitPullRequestArrow } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, Badge, formatDateTime, Spinner } from '../components/admin/AdminUI';

const SEVERITY_TONE = { low: 'gray', medium: 'amber', high: 'red' };
const STATUS_TONE = {
  new: 'violet', triaged: 'blue', in_progress: 'amber',
  resolved: 'emerald', wont_fix: 'gray',
};
const STATUSES = ['', 'new', 'triaged', 'in_progress', 'resolved', 'wont_fix'];
const SEVERITIES = ['', 'low', 'medium', 'high'];

const AdminBugReports = () => {
  const { toast } = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [selected, setSelected] = useState(null); // detail row
  const [detailLoading, setDetailLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { document.title = "Skilldeck — Admin · Bugs"; }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bugReportsAPI.list({
        status: status || undefined,
        severity: severity || undefined,
        limit: 200,
      });
      setReports(data?.reports || []);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les bug reports', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [status, severity, toast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetailLoading(true);
    setSelected({ ...row, _loading: true });
    try {
      const full = await bugReportsAPI.get(row.id);
      setSelected(full);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger le détail', variant: 'destructive' });
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const syncFromGitHub = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await bugReportsAPI.syncFromGitHub();
      toast({
        title: 'Sync GitHub terminée',
        description:
          `${res.checked} vérifiés · ${res.updated} mis à jour` +
          (res.deleted_on_github ? ` · ${res.deleted_on_github} supprimés sur GitHub` : '') +
          (res.errors ? ` · ${res.errors} erreurs` : ''),
      });
      await load();
    } catch (e) {
      toast({
        title: 'Sync GitHub impossible',
        description: e?.response?.data?.detail || 'GITHUB_REPO / GITHUB_TOKEN sont-ils configurés ?',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      const updated = await bugReportsAPI.update(selected.id, { status: newStatus });
      setSelected({ ...selected, ...updated });
      setReports((rs) => rs.map((r) => (r.id === selected.id ? { ...r, ...updated } : r)));
      toast({ title: 'Statut mis à jour' });
    } catch {
      toast({ title: 'Erreur', description: 'Mise à jour impossible', variant: 'destructive' });
    }
  };

  return (
    <AdminLayout title="Bug reports">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-3 flex-wrap">
            <Bug className="h-4 w-4 text-violet-300" />
            <h3 className="text-sm font-semibold text-white">Bug reports beta ({reports.length})</h3>
            <div className="ml-auto flex items-center gap-2">
              <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                <Filter className="h-3 w-3" /> Statut
              </div>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
                {STATUSES.map((s) => <option key={s} value={s}>{s || 'Tous'}</option>)}
              </select>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                className="px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s ? `Sévérité: ${s}` : 'Toutes sévérités'}</option>)}
              </select>
              <button onClick={syncFromGitHub} disabled={syncing}
                title="Tire l'état actuel de chaque issue GitHub liée et met à jour le statut local"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white hover:border-violet-600/40 disabled:opacity-50">
                <GitPullRequestArrow className="h-3 w-3" />
                {syncing ? 'Sync…' : 'Synchroniser GitHub'}
              </button>
              <button onClick={load}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white hover:border-violet-600/40">
                <RefreshCcw className="h-3 w-3" /> Recharger
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : reports.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500">Aucun bug report.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#05050f] text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium">Réf</th>
                    <th className="text-left px-5 py-2 font-medium">Sévérité</th>
                    <th className="text-left px-5 py-2 font-medium">Statut</th>
                    <th className="text-left px-5 py-2 font-medium">Date</th>
                    <th className="text-left px-5 py-2 font-medium">Résumé</th>
                    <th className="text-left px-5 py-2 font-medium">Page</th>
                    <th className="text-left px-5 py-2 font-medium">GH</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.08)]">
                  {reports.map((r) => (
                    <tr key={r.id} onClick={() => openDetail(r)}
                      className="cursor-pointer hover:bg-[rgba(108,71,255,.06)]">
                      <td className="px-5 py-2.5 font-mono text-violet-300 text-xs">{r.ref}</td>
                      <td className="px-5 py-2.5"><Badge tone={SEVERITY_TONE[r.severity] || 'gray'}>{r.severity}</Badge></td>
                      <td className="px-5 py-2.5"><Badge tone={STATUS_TONE[r.status] || 'gray'}>{r.status}</Badge></td>
                      <td className="px-5 py-2.5 text-gray-400 text-xs">{formatDateTime(r.created_at)}</td>
                      <td className="px-5 py-2.5 text-white truncate max-w-md">{(r.user_description || '').slice(0, 100)}</td>
                      <td className="px-5 py-2.5 text-gray-500 text-xs font-mono">{r.route || '—'}</td>
                      <td className="px-5 py-2.5">
                        {r.github_issue_number ? (
                          <a href={r.github_issue_url} target="_blank" rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200 text-xs">
                            #{r.github_issue_number} <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : <span className="text-gray-600 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="ml-auto w-full max-w-2xl h-full overflow-y-auto bg-[#05050f] border-l border-[rgba(108,71,255,.2)] shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#05050f] border-b border-[rgba(108,71,255,.15)] px-6 py-4 flex items-center gap-3">
              <Bug className="h-4 w-4 text-violet-300" />
              <span className="text-sm font-semibold text-white font-mono">{selected.ref}</span>
              <Badge tone={SEVERITY_TONE[selected.severity] || 'gray'}>{selected.severity}</Badge>
              <Badge tone={STATUS_TONE[selected.status] || 'gray'}>{selected.status}</Badge>
              <button onClick={() => setSelected(null)} className="ml-auto text-gray-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-16"><Spinner /></div>
            ) : (
              <div className="p-6 space-y-5 text-sm">
                <section>
                  <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Description</h4>
                  <p className="text-white whitespace-pre-wrap">{selected.user_description}</p>
                </section>

                <section className="grid grid-cols-2 gap-3 text-xs">
                  <Meta label="Page"           value={selected.route} mono />
                  <Meta label="Version"        value={selected.app_version} mono />
                  <Meta label="Request ID"     value={selected.request_id} mono />
                  <Meta label="Dossier ID"     value={selected.dossier_id} mono />
                  <Meta label="User ID"        value={selected.user_id} mono />
                  <Meta label="Org ID"         value={selected.org_id} mono />
                  <Meta label="User-Agent"     value={selected.user_agent} className="col-span-2 text-[11px]" />
                </section>

                {selected.screenshot_url && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Capture d'écran</h4>
                    <a href={selected.screenshot_url} target="_blank" rel="noreferrer">
                      <img src={selected.screenshot_url} alt="screenshot"
                        className="w-full rounded border border-[rgba(108,71,255,.2)]" />
                    </a>
                  </section>
                )}

                {selected.error_stack && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Stack</h4>
                    <pre className="text-[11px] text-amber-200 bg-black/40 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                      {selected.error_stack}
                    </pre>
                  </section>
                )}

                {selected.breadcrumbs?.length > 0 && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                      Breadcrumbs ({selected.breadcrumbs.length})
                    </h4>
                    <ul className="space-y-1 text-[11px] text-gray-300 font-mono">
                      {selected.breadcrumbs.slice(-15).map((b, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-gray-600">{b.ts?.slice(11, 19)}</span>
                          <span className="text-violet-300">{b.type}</span>
                          <span className="text-gray-400 truncate">{JSON.stringify(b.payload)}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {selected.network_errors?.length > 0 && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                      Network errors ({selected.network_errors.length})
                    </h4>
                    <ul className="space-y-2 text-[11px]">
                      {selected.network_errors.slice(-10).map((n, i) => (
                        <li key={i} className="bg-black/40 p-2 rounded">
                          <div className="text-white font-mono">{n.method} {n.url} → {n.status}</div>
                          {n.requestId && <div className="text-gray-500">req: {n.requestId}</div>}
                          {n.body && <pre className="text-amber-200 mt-1 whitespace-pre-wrap">{n.body}</pre>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {selected.console_errors?.length > 0 && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                      Console errors ({selected.console_errors.length})
                    </h4>
                    <ul className="space-y-1 text-[11px] text-red-300 font-mono">
                      {selected.console_errors.slice(-5).map((c, i) => (
                        <li key={i} className="bg-black/40 p-2 rounded whitespace-pre-wrap">{c.message}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {selected.dossier_snapshot && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Dossier snapshot</h4>
                    <details>
                      <summary className="cursor-pointer text-violet-300 text-xs">Voir JSON</summary>
                      <pre className="mt-2 text-[10px] text-gray-300 bg-black/40 p-2 rounded overflow-x-auto max-h-80">
                        {JSON.stringify(selected.dossier_snapshot, null, 2)}
                      </pre>
                    </details>
                  </section>
                )}

                <section className="border-t border-[rgba(108,71,255,.15)] pt-4 flex items-center gap-2 flex-wrap">
                  {selected.github_issue_url && (
                    <a href={selected.github_issue_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-violet-600 text-white text-xs hover:bg-violet-700">
                      Ouvrir l'issue GitHub <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <select value={selected.status} onChange={(e) => updateStatus(e.target.value)}
                    className="px-3 py-1.5 bg-[#0a0a1e] border border-[rgba(108,71,255,.15)] rounded text-xs text-white">
                    {['new', 'triaged', 'in_progress', 'resolved', 'wont_fix'].map((s) =>
                      <option key={s} value={s}>{s}</option>
                    )}
                  </select>
                </section>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Meta = ({ label, value, mono = false, className = '' }) => (
  <div className={className}>
    <div className="text-gray-500">{label}</div>
    <div className={`text-white ${mono ? 'font-mono' : ''} break-all`}>{value || '—'}</div>
  </div>
);

export default AdminBugReports;
